import { z } from 'zod';
import { Status } from '@pillar-portal/database/dist/frontend';
import type {
    LegalDocumentsSearchRequest,
    UpdateLegalDocumentStatusRequest,
} from '../legal-documents.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    AuthorizationPaginationLimitQuerySchema,
} from './authorization.schemas';
import { NonEmptyStringSchema } from './common.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';

export const LegalDocumentUuidSchema: z.ZodType<string> = z.string().uuid();

export const LegalDocumentUuidParamSchema: z.ZodType<string> = LegalDocumentUuidSchema;

export const LegalDocumentsOpaqueCursorQuerySchema: z.ZodType<string | undefined> = z
    .string()
    .trim()
    .min(1)
    .optional();

export const LegalDocumentsSearchRequestSchema: z.ZodType<LegalDocumentsSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: LegalDocumentsOpaqueCursorQuerySchema,
    })
    .strict();

const legalDocumentListItemSchemaObject = z.object({
    legal_document_uuid: LegalDocumentUuidSchema,
    legal_document_title: NonEmptyStringSchema,
    legal_document_version: NonEmptyStringSchema,
    status: z.enum(Status),
    legal_document_content_type: NonEmptyStringSchema,
    in_force_start: z.string().datetime(),
    in_force_end: z.string().datetime().nullable(),
    required_for_users: z.boolean(),
    required_for_orgs: z.boolean(),
    content_sha256: NonEmptyStringSchema,
    source_filename: z.string().optional(),
    content_length_bytes: z.number().int().nonnegative(),
});
export const LegalDocumentListItemSchema: typeof legalDocumentListItemSchemaObject =
    legalDocumentListItemSchemaObject;

const searchLegalDocumentsPageResponseSchemaObject = z.object({
    legal_documents: z.array(LegalDocumentListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});
export const SearchLegalDocumentsPageResponseSchema:
    typeof searchLegalDocumentsPageResponseSchemaObject =
        searchLegalDocumentsPageResponseSchemaObject;

const createLegalDocumentRequestSchemaObject = z.object({
    legal_document_title: NonEmptyStringSchema,
    legal_document_version: NonEmptyStringSchema,
    legal_document_text: NonEmptyStringSchema,
    legal_document_content_type: NonEmptyStringSchema,
    in_force_start: z.string().datetime(),
    in_force_end: z.string().datetime().nullable().optional(),
    required_for_users: z.boolean(),
    required_for_orgs: z.boolean(),
    source_filename: z.string().optional(),
});
export const CreateLegalDocumentRequestSchema: typeof createLegalDocumentRequestSchemaObject =
    createLegalDocumentRequestSchemaObject;

const createLegalDocumentResponseSchemaObject = z.object({
    legal_document_uuid: LegalDocumentUuidSchema,
});
export const CreateLegalDocumentResponseSchema: typeof createLegalDocumentResponseSchemaObject =
    createLegalDocumentResponseSchemaObject;

const getLegalDocumentResponseSchemaObject = z.object({
    legal_document: LegalDocumentListItemSchema.extend({
        legal_document_text: NonEmptyStringSchema,
    }),
});
export const GetLegalDocumentResponseSchema: typeof getLegalDocumentResponseSchemaObject =
    getLegalDocumentResponseSchemaObject;

const listPendingRequiredLegalDocumentsResponseSchemaObject = z.object({
    legal_documents: z.array(LegalDocumentListItemSchema),
});
export const ListPendingRequiredLegalDocumentsResponseSchema:
    typeof listPendingRequiredLegalDocumentsResponseSchemaObject =
        listPendingRequiredLegalDocumentsResponseSchemaObject;

const acceptLegalDocumentRequestSchemaObject = z.object({
    accepted_role_assignment_uuid: z.string().uuid().optional(),
});
export const AcceptLegalDocumentRequestSchema: typeof acceptLegalDocumentRequestSchemaObject =
    acceptLegalDocumentRequestSchemaObject;

export const LegalDocumentMutableStatusSchema: z.ZodType<
    UpdateLegalDocumentStatusRequest['status']
> = z.union([z.literal(Status.ACTIVE), z.literal(Status.DISABLED)]);

const updateLegalDocumentStatusRequestSchemaObject = z.object({
    status: LegalDocumentMutableStatusSchema,
});
export const UpdateLegalDocumentStatusRequestSchema:
    typeof updateLegalDocumentStatusRequestSchemaObject =
        updateLegalDocumentStatusRequestSchemaObject;
